// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

/**
 * Look-alikes: a browsing aid, and deliberately NOT a duplicate detector.
 *
 * THIS FILE EXISTS BECAUSE THE OBVIOUS DESIGN IS WRONG, AND IT WAS MEASURED TO BE WRONG.
 *
 * The natural thing to build is: embed everything, threshold the cosine, union-find the pairs,
 * throw away all but one per cluster. Every "AI image deduplicator" works like that. It cannot
 * work, and the numbers are not close.
 *
 * Measured with this exact model (MobileCLIP-S0 fp16) on google/dreambooth — the canonical
 * DreamBooth/LoRA subject set, 30 subjects, 158 hand-curated photographs, containing ZERO
 * duplicates by construction:
 *
 *   Different photos of the SAME subject (must be KEPT):  median 0.804, p95 0.934, p99 0.972,
 *                                                          max 0.9916   (n = 342 pairs)
 *   Different subjects entirely:                           median 0.243, p95 0.536, max 0.919
 *
 * And against genuine duplicates generated from those same photos, the worst case per transform:
 *
 *   2x upscale 0.981 | re-encode q40 0.956 | brightness +30% 0.931 | crop 10%/edge 0.926 |
 *   50% resize 0.924 | 12px border 0.903 | 25% resize 0.889 | 4px blur 0.888 |
 *   HORIZONTAL FLIP 0.878 | 5-degree rotation 0.860 | greyscale 0.855 | crop 20%/edge 0.853 |
 *   watermark 0.726
 *
 * The two distributions overlap almost completely, and they overlap the WRONG WAY ROUND: true
 * duplicates bottom out around 0.73-0.88 while genuinely different photographs of one subject
 * reach 0.99. There is no cutoff that catches duplicates without deleting the dataset:
 *
 *   at 0.90 it misses flips, rotations, greyscale, crops, resizes and watermarks, AND falsely
 *           condemns 49 of the 342 same-subject pairs
 *   at 0.98 it misses twelve of the thirteen duplicate families, catching only a 2x upscale —
 *           which the perceptual hash already finds at a Hamming distance of 1
 *
 * Union-find made it worse still: at 0.90 it collapsed six visually distinct photographs of one
 * bowl into a single cluster, via a chain containing a pair scoring 0.809 — nine hundredths BELOW
 * the threshold that formed the cluster. 35 of 158 hand-curated images gone.
 *
 * So the embedding stays, because ranking images by how alike they look is genuinely useful for a
 * human going through a folder. It just never gets to decide anything. It produces PAIRS, sorted,
 * with nothing pre-selected — and the interface says why.
 */

import type { ImageRecord } from './types.ts';
import { EmbeddingMatrix, pairsAboveCosine } from './vectors.ts';
import { hamming } from './image.ts';

export interface SimilarPair {
  readonly a: number;
  readonly b: number;
  readonly cosine: number;
  /** Hamming distance too, so the review row can say whether the hash also thinks they match. */
  readonly hamming: number;
}

/**
 * The strongest look-alike pairs, most similar first.
 *
 * Capped, because this is a review list a person reads — the hundredth-best pair on a 2,000-image
 * folder is not going to be looked at, and rendering it costs a thumbnail decode. The cap is
 * reported to the caller rather than applied silently.
 */
export function findSimilarPairs(
  records: readonly ImageRecord[],
  matrix: EmbeddingMatrix,
  threshold: number,
  limit: number,
  alreadyGrouped: (a: number, b: number) => boolean,
): { pairs: SimilarPair[]; total: number } {
  const all: SimilarPair[] = [];
  pairsAboveCosine(matrix, threshold, (ia, ib, c) => {
    const a = records[ia];
    const b = records[ib];
    if (!a || !b) return;
    // A pair the perceptual hash already put in one group is not news — it is in the duplicates
    // list above, already handled, and repeating it here would bury the pairs only the model found.
    if (alreadyGrouped(a.id, b.id)) return;
    all.push({ a: a.id, b: b.id, cosine: c, hamming: hamming(a.hash, b.hash) });
  });
  all.sort((x, y) => y.cosine - x.cosine || x.a - y.a || x.b - y.b);
  return { pairs: all.slice(0, limit), total: all.length };
}

/**
 * How to describe a similarity score to somebody deciding whether to drop a photograph.
 *
 * Every band names what was actually measured at that level, because a bare number invites the
 * reader to invent a meaning for it — and the meaning they would invent ("0.95 means duplicate")
 * is the one the data rules out.
 */
export function describeCosine(c: number): string {
  if (c >= 0.985) return 'nearly identical — but a 2x upscale of the same photo scores about this, and so do some genuinely different frames';
  if (c >= 0.96) return 'very alike — different photos of one subject reach 0.99, so this is not evidence of a duplicate';
  if (c >= 0.90) return 'alike — around here sit both re-encoded copies and quite different shots of the same thing';
  return 'loosely alike — same kind of subject, probably not the same picture';
}
